/**
 * Encode/decode the Home search state to and from the URL query string.
 *
 * The URL is the source of truth for Home's search so that browser Back
 * restores it (search → recipe → back keeps the results), and searches are
 * shareable/bookmarkable. Encoding:
 *   - `q`        — search text (trimmed; omitted when blank)
 *   - <group>    — one repeated param per tag group, keyed by the lowercased
 *                  group name, e.g. `cuisine=Italian&cuisine=Mexican&diet=Vegan`
 *   - `rating`   — one repeated param per selected rating, e.g. `rating=4&rating=5`
 *
 * Decoding is defensive: unknown groups, unknown tags, and non-numeric /
 * out-of-range ratings are dropped so a malformed or hand-edited URL never
 * crashes Home.
 */

import { PREDEFINED_TAG_GROUPS } from '@/constants/tags';

export interface HomeSearchState {
  searchTerm: string;
  selectedTagsByGroup: Record<string, string[]>;
  selectedRatings: number[];
}

/** Lowercased-group-param → { canonical group name, valid tag set }. */
const GROUP_BY_PARAM = new Map<string, { name: string; tags: Set<string> }>();
for (const group of PREDEFINED_TAG_GROUPS) {
  GROUP_BY_PARAM.set(group.name.toLowerCase(), {
    name: group.name,
    tags: new Set(group.tags),
  });
}

const MIN_RATING = 1;
const MAX_RATING = 5;

/** Build a URLSearchParams from the current Home search state. */
export function encodeHomeSearch(state: HomeSearchState): URLSearchParams {
  const params = new URLSearchParams();

  const q = state.searchTerm.trim();
  if (q) params.set('q', q);

  // Iterate the predefined groups (not the state keys) for deterministic order.
  for (const group of PREDEFINED_TAG_GROUPS) {
    const tags = state.selectedTagsByGroup[group.name];
    if (!tags?.length) continue;
    for (const tag of tags) params.append(group.name.toLowerCase(), tag);
  }

  for (const rating of state.selectedRatings) params.append('rating', String(rating));

  return params;
}

/** Parse Home search state from a URLSearchParams, dropping anything invalid. */
export function decodeHomeSearch(params: URLSearchParams): HomeSearchState {
  const searchTerm = (params.get('q') ?? '').trim();

  const selectedTagsByGroup: Record<string, string[]> = {};
  for (const [param, group] of GROUP_BY_PARAM) {
    const valid = params.getAll(param).filter((tag) => group.tags.has(tag));
    const unique = [...new Set(valid)];
    if (unique.length) selectedTagsByGroup[group.name] = unique;
  }

  const ratings = params
    .getAll('rating')
    .map((value) => Number(value))
    .filter((n) => Number.isInteger(n) && n >= MIN_RATING && n <= MAX_RATING);
  const selectedRatings = [...new Set(ratings)].sort((a, b) => a - b);

  return { searchTerm, selectedTagsByGroup, selectedRatings };
}

/**
 * A canonical, order-independent string for the state — used as the key when
 * saving/restoring scroll position. Two states that filter to the same results
 * produce the same key regardless of the order tags/ratings were toggled.
 */
export function homeSearchKey(state: HomeSearchState): string {
  const parts: string[] = [];

  const q = state.searchTerm.trim();
  if (q) parts.push(`q=${q}`);

  for (const group of PREDEFINED_TAG_GROUPS) {
    const tags = state.selectedTagsByGroup[group.name];
    if (tags?.length) {
      parts.push(`${group.name.toLowerCase()}=${[...tags].sort().join(',')}`);
    }
  }

  if (state.selectedRatings.length) {
    parts.push(`rating=${[...state.selectedRatings].sort((a, b) => a - b).join(',')}`);
  }

  return parts.join('&');
}
